import { doc, setDoc, getDoc, collection, getDocs, query, orderBy, limit, Timestamp, where } from "firebase/firestore";
import { db } from "../firebase";

class GoalAttempt {
  constructor(userId, goalId, data) {
    this.userId = userId;
    this.goalId = goalId;
    this.date = data.date; // "YYYY-MM-DD"
    this.performedAt = data.performedAt || Timestamp.now();
    this.value = data.value;
    this.reps = data.reps || null;
    this.isPersonalBest = data.isPersonalBest || false;
    this.notes = data.notes || "";
    this.perceivedEffort = data.perceivedEffort || null;
    this.createdAt = data.createdAt || Timestamp.now();
  }

  static getCollectionPath(userId) {
    return `goals/${userId}/attempts`;
  }

  // Create a new attempt
  static async create(userId, goalId, goal, data) {
    const attemptId = `${goalId}_${data.date}_${Date.now()}`;
    const attemptRef = doc(db, GoalAttempt.getCollectionPath(userId), attemptId);

    // Check if this is a personal best
    const existingAttempts = await GoalAttempt.getByGoalId(userId, goalId);
    let isPersonalBest = false;

    if (existingAttempts.length === 0) {
      isPersonalBest = true;
    } else if (goal.direction === "increase") {
      const currentBest = Math.max(...existingAttempts.map(a => a.value));
      isPersonalBest = data.value > currentBest;
    } else {
      const currentBest = Math.min(...existingAttempts.map(a => a.value));
      isPersonalBest = data.value < currentBest;
    }

    const attemptData = {
      goalId,
      date: data.date,
      performedAt: Timestamp.now(),
      value: data.value,
      reps: data.reps || null,
      isPersonalBest,
      notes: data.notes || "",
      perceivedEffort: data.perceivedEffort || null,
      createdAt: Timestamp.now(),
    };

    await setDoc(attemptRef, attemptData);
    return attemptId;
  }

  // Get all attempts for a specific goal
  static async getByGoalId(userId, goalId) {
    const attemptsRef = collection(db, GoalAttempt.getCollectionPath(userId));
    const q = query(
      attemptsRef,
      where("goalId", "==", goalId),
      orderBy("performedAt", "desc")
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  // Get the most recent attempt for a goal
  static async getMostRecent(userId, goalId) {
    const attemptsRef = collection(db, GoalAttempt.getCollectionPath(userId));
    const q = query(
      attemptsRef,
      where("goalId", "==", goalId),
      orderBy("performedAt", "desc"),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  }

  // Get personal best for a goal
  static async getPersonalBest(userId, goalId, direction = "increase") {
    const attempts = await GoalAttempt.getByGoalId(userId, goalId);

    if (attempts.length === 0) {
      return null;
    }

    if (direction === "increase") {
      return attempts.reduce((best, attempt) =>
        attempt.value > best.value ? attempt : best
      );
    } else {
      return attempts.reduce((best, attempt) =>
        attempt.value < best.value ? attempt : best
      );
    }
  }

  // Get stats for a goal
  static async getStats(userId, goalId, goal) {
    const attempts = await GoalAttempt.getByGoalId(userId, goalId);

    if (attempts.length === 0) {
      return {
        totalAttempts: 0,
        currentValue: goal.startingValue || null,
        personalBest: null,
        personalBestDate: null,
        latestAttempt: null,
      };
    }

    const latestAttempt = attempts[0];
    const personalBest = goal.direction === "increase"
      ? Math.max(...attempts.map(a => a.value))
      : Math.min(...attempts.map(a => a.value));
    const pbAttempt = attempts.find(a => a.value === personalBest);

    return {
      totalAttempts: attempts.length,
      currentValue: latestAttempt.value,
      personalBest,
      personalBestDate: pbAttempt?.date,
      latestAttempt,
    };
  }

  // Check if goal is completed
  static isGoalCompleted(goal, currentValue) {
    if (goal.direction === "increase") {
      return currentValue >= goal.targetValue;
    } else {
      return currentValue <= goal.targetValue;
    }
  }
}

export default GoalAttempt;
